import { getValidAccessToken } from './tokenRefresh';
import { isAuthenticated } from './userSession';

/**
 * API Fetch Utility
 * Handles API requests with automatic token management (requires authentication)
 */

/**
 * API request options
 * @typedef {Object} ApiOptions
 * @property {string} url - API endpoint URL
 * @property {string} [method='GET'] - HTTP method
 * @property {any} [body] - Request body
 * @property {Object} [headers] - Additional headers
 * @property {Object} [params] - Query parameters
 * @property {string} [locale] - Locale for Accept-Language header
 * @property {boolean} [useTimeout=false] - Whether to use timeout
 */

/**
 * Make an API request with automatic token management
 * @param {ApiOptions} options - Request options
 * @returns {Promise<Response>} Fetch response
 */
export async function apiFetch(options) {
    const {
        url,
        method = 'GET',
        body,
        headers = {},
        params,
        locale,
        useTimeout = false
    } = options;

    // Require authentication for API calls
    if (!isAuthenticated()) {
        throw new Error('User must be authenticated to make API calls');
    }

    // Get user access token
    const accessToken = await getValidAccessToken();
    if (!accessToken) {
        throw new Error('Failed to get valid access token');
    }

    // Build URL with query parameters
    let fullUrl = url;
    const baseUrl = process.env.REACT_APP_API_BASE_URL ||
        process.env.REACT_APP_BASE_URL?.replace(/=+$/, '') ||
        '';

    if (baseUrl && !url.startsWith('http')) {
        fullUrl = `${baseUrl}${url}`;
    }

    if (params) {
        const queryString = new URLSearchParams(params).toString();
        fullUrl += (fullUrl.includes('?') ? '&' : '?') + queryString;
    }

    // Build headers
    const requestHeaders = new Headers({
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        ...headers
    });

    if (locale) {
        requestHeaders.append('Accept-Language', locale);
    }

    // Set up timeout if needed
    let controller = null;
    let timeoutId = null;

    if (useTimeout) {
        controller = new AbortController();
        timeoutId = setTimeout(() => controller.abort(), 10000);
    }

    try {
        const response = await fetch(fullUrl, {
            method,
            headers: requestHeaders,
            body: body ? JSON.stringify(body) : undefined,
            signal: controller?.signal
        });

        if (timeoutId) clearTimeout(timeoutId);

        return response;
    } catch (error) {
        if (timeoutId) clearTimeout(timeoutId);
        console.error(`[API] Error in request to ${fullUrl}:`, error);
        throw error;
    }
}
